// external imports
import { Registry } from "react-registry";
import ObjectMap from "react-registry/dist/util/ObjectMap";

// internal imports
import Service from "core/decorators/Service";
import Class from "core/util/Class";
import ConfigUtil from "core/util/config/ConfigUtil";
import Logger from "core/util/Logger";
import StringUtil from "core/util/StringUtil";
import {default as TypeUtil, iDef } from "core/util/TypeUtils";
import ILocalCacheService from "../ILocalCacheService";

/**
 *
 * @overview: Service for caching objects and literals (only) on the user's local machine. If you need
 * to store classes / functions try using {@link Registry} instead. This service differs
 * from redux in that it has various retention options and is primarily used for service
 * layer operations. Use in the panel and view layers should be avoided. Ideally this will
 * aid in decoupling the service layer form redux and react.
 *
 * @author: jesposito
 *
 * Usage:
 *
 * 	{@code const cacheService = ServiceRegistry.get(ILocalCacheService); }
 *
 * 	// get an object
 * 	{@code const myObject = cacheService.get("my-cache", "my-key") }
 *
 * 	// store an item
 * 	{@code const myObject = {foo: "bar"}}
 *  {@code cacheService.put("myCache", "my-key", myObject)
 *
 * 	// store an item that persists after the page is refreshed.
 *  {@code cacheService.put("my-cache", "my-key", myObject, RetentionPolicy.LOCAL_STORAGE);
 *
 */

interface CacheStructure {
	caches: ObjectMap;
}

interface PrimitiveStore {
	value: string | number | boolean;
}

@Service()
export default class StorageService extends ILocalCacheService {
	private static MAX_EXPIRY: number = 3600000; // 12 hours

	public private: Cache;

	constructor() {
		super();

		// 1. look for local storage for previous cache implementation
	}

	private static getCacheKey(key: string, cacheName?: string, ): string {
		// prefice the key with the id of the current environment so we can have multiple "sessions" for each.
		const environment = ConfigUtil.getEnvName();

		if(iDef(cacheName)) {
			return StringUtil.format("%s:%s:%s", environment, cacheName, key);
		}

		return StringUtil.format("%s:%s", environment, key);
	}

	public getString(key: string, defaultValue?: string | undefined, cacheName?: string): string | undefined {
		const result = this.get(key, { value: defaultValue}, cacheName) as PrimitiveStore;
		return result.value as string;
	}

	public getNumber(key: string, defaultValue?: number | undefined, cacheName?: string): number | undefined {
		const result = this.get(key, { value: defaultValue}, cacheName) as PrimitiveStore;
		return result.value as number;
	}


	public getAs<T>(key: string, clazz: Class<T>, cacheName?: string): T {
		const result = this.get(key, undefined, cacheName);

		if(iDef(result)) {
			return TypeUtil.become(result, clazz);
		}

		return new clazz();
	}

	public get<T = object>(key: string, defaultValue?: T | undefined, cacheName?: string): T | undefined {

		try {
			const result = localStorage.getItem(StorageService.getCacheKey(key, cacheName));

			if(result) {
				return JSON.parse(result) as T;
			}

		} catch(e) {
			Logger.error("Cannot access localStorage: %s", e);
		}

		return defaultValue;
	}

	public putString(key: string, payload: string, cacheName?: string): boolean {
		return this.put(key, { value: payload });
	}

	public putNumber(key: string, payload: number, cacheName?: string): boolean {
		return this.put(key, { value: payload });
	}

	public put(key: string, payload: object, cacheName?: string): boolean {
		const serialized = JSON.stringify(payload);

		try {
			localStorage.setItem(StorageService.getCacheKey(key, cacheName), serialized);
			return true;
		} catch(e) {
			Logger.error("Cannot access localStorage: %s", e);
		}

		return false;
	}

	public remove(key: string, cacheName?: string): boolean {

		try {
			localStorage.removeItem(StorageService.getCacheKey(key, cacheName));
			return true;
		} catch(e) {
			Logger.error("Cannot access localStorage, %s", e);
		}

		return false;
	}

	public getServiceId(): string {
		return ILocalCacheService.DEFAULT_ID;
	}
}



// WEBPACK FOOTER //
// ./src/core/service/cache/impl/LocalCache.service.ts